import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/language-context';
import { useToast } from '../contexts/toast-context';

/** Set to true to show the “Palaikyk…” donation panel on the home hero again. */
const SHOW_HOME_SUPPORT_PANEL = false;

function formatAmountForQuery(eur) {
  if (Number.isInteger(eur)) return String(eur);
  const s = eur.toFixed(2);
  return s.replace(/\.?0+$/, '');
}

function SupportDonationBlock() {
  const { t } = useLanguage();
  const { showError } = useToast();
  const navigate = useNavigate();
  const [tier, setTier] = useState('0.5');
  const [customEur, setCustomEur] = useState('');

  const tiers = [
    { id: '0.5', label: t('donateAmount050') },
    { id: '1', label: t('donateAmount1') },
    { id: '2', label: t('donateAmount2') },
    { id: 'custom', label: t('donateAmountCustom') },
  ];

  function resolveAmountEur() {
    if (tier === 'custom') {
      const raw = String(customEur).trim().replace(',', '.');
      const n = parseFloat(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    return parseFloat(tier);
  }

  function handleParemti() {
    const amount = resolveAmountEur();
    if (tier === 'custom' && amount == null) {
      showError(t('donateCustomInvalid'));
      return;
    }
    if (amount == null || amount <= 0) {
      showError(t('donateCustomInvalid'));
      return;
    }
    const q = formatAmountForQuery(amount);
    navigate(`/kontaktai?parama=${encodeURIComponent(q)}`);
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:gap-2.5" role="group" aria-label={t('donateButton')}>
        {tiers.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTier(id)}
            className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-dark-900 ${
              tier === id
                ? 'border-primary-500 bg-primary-600/25 text-white shadow-inner'
                : 'border-dark-600 bg-dark-900/50 text-gray-200 hover:border-dark-500 hover:bg-dark-800/80'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tier === 'custom' && (
        <input
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder={t('donateCustomPlaceholder')}
          value={customEur}
          onChange={(e) => setCustomEur(e.target.value)}
          className="w-full rounded-xl border border-dark-600 bg-dark-900/80 px-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
        />
      )}
      <button
        type="button"
        onClick={handleParemti}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-md shadow-primary-900/50 transition-colors hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-dark-900"
      >
        {t('donateButton')}
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
        </svg>
      </button>
    </div>
  );
}

/**
 * Home landing hero with angled split: main headline + stats (left) and donation panel (right on lg+).
 * Diagonal edge uses clip-path — slanted top on mobile, slanted seam on desktop.
 */
export function HomeHeroSection({ hero, children }) {
  const hasSupportPanel = SHOW_HOME_SUPPORT_PANEL;

  return (
    <section className="relative pt-8 pb-8 sm:pt-12 sm:pb-12 lg:pt-16 lg:pb-10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={
            hasSupportPanel
              ? 'flex flex-col lg:flex-row lg:items-stretch lg:gap-0'
              : 'flex flex-col items-center'
          }
        >
          <div
            className={
              hasSupportPanel
                ? 'relative z-10 flex-1 min-w-0 pb-10 lg:pb-0 lg:pr-6 xl:pr-10 text-center lg:text-left lg:flex lg:flex-col lg:justify-center'
                : 'relative z-10 w-full max-w-4xl mx-auto text-center flex flex-col items-center pb-8 sm:pb-10 lg:pb-0'
            }
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6">
              {hero.title}{' '}
              <span className="text-gradient">{hero.highlight}</span>
            </h1>
            <p
              className={
                hasSupportPanel
                  ? 'text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto lg:mx-0 px-0 sm:px-4 lg:px-0'
                  : 'text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto px-0 sm:px-4'
              }
            >
              {hero.subtitle}
            </p>
            <div
              className={
                hasSupportPanel
                  ? 'flex flex-wrap justify-center lg:justify-start gap-4 sm:gap-6 lg:gap-8 text-center'
                  : 'flex flex-wrap justify-center gap-4 sm:gap-6 lg:gap-8 text-center'
              }
            >
              {children}
            </div>
          </div>

          {SHOW_HOME_SUPPORT_PANEL && (
            <aside
              className={[
                'relative w-full max-lg:mt-2 lg:w-[min(100%,420px)] lg:flex-shrink-0 lg:min-w-0',
                'lg:-ml-[7%] xl:-ml-[5%]',
                '[clip-path:polygon(0_12%,100%_0,100%_100%,0_100%)]',
                'lg:[clip-path:polygon(18%_0,100%_0,100%_100%,0_100%)]',
              ].join(' ')}
            >
              <div
                className={[
                  'h-full min-h-[260px] lg:min-h-full',
                  'bg-gradient-to-br from-primary-900/85 via-dark-800/95 to-dark-900',
                  'border border-primary-500/25 shadow-lg shadow-primary-900/50',
                  'backdrop-blur-sm',
                ].join(' ')}
              >
                <div className="relative h-full flex flex-col justify-center px-6 sm:px-8 pt-14 pb-8 max-lg:pt-16 lg:pt-8 lg:pb-8 lg:pl-16 xl:pl-20">
                  <div
                    className="absolute top-4 right-5 lg:top-6 lg:right-6 opacity-90"
                    aria-hidden
                  >
                    <svg
                      className="w-9 h-9 text-primary-400/90"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                      />
                    </svg>
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-white mb-2 pr-10">
                    Palaikyk nemokamą FilmųDB
                  </h2>
                  <p className="text-sm sm:text-[15px] text-gray-300 leading-relaxed mb-4">
                    Tavo parama padeda išlaikyti svetainę{' '}
                    <span className="text-white font-medium">nemokamą ir be invazinių reklamų</span>
                    — be pop-upų, be mokamų sienų, tik kino atradimui.
                  </p>
                  <ul className="text-xs sm:text-sm text-gray-400 space-y-2 mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-accent-green mt-0.5 shrink-0" aria-hidden>
                        ✓
                      </span>
                      <span>Serveriai, atnaujinimai ir saugumas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-accent-green mt-0.5 shrink-0" aria-hidden>
                        ✓
                      </span>
                      <span>Projektas lieka nepriklausomas nuo reklamos tinklų</span>
                    </li>
                  </ul>
                  <SupportDonationBlock />
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>
    </section>
  );
}
